import { useEffect, useState } from 'react';
import type { ReviewSummary, ReviewState } from './types';
import { FindingSummary } from './FindingSummary';
import { StuckIndicator } from './StuckIndicator';

interface ReviewListProps {
  reviews: ReviewSummary[];
  onSelect: (sessionId: string) => void;
  /** Spec 188 Phase 3 — invoked when the retry button is clicked on a row.
   * When omitted, retry buttons are hidden. */
  onRetry?: (sessionId: string, fromStep: string) => void;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  return remSec > 0 ? `${minutes}m ${remSec}s` : `${minutes}m`;
}

function formatCost(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

const STATE_STYLES: Record<ReviewState, { color: string; icon: string }> = {
  idle: { color: 'text-gray-400', icon: '\u25cb' },
  pending: { color: 'text-gray-400', icon: '\u25cb' },
  fetching: { color: 'text-blue-400', icon: '\u25cf' },
  reviewing: { color: 'text-blue-400', icon: '\u25cf' },
  converging: { color: 'text-blue-400', icon: '\u25cf' },
  paused: { color: 'text-yellow-400', icon: '\u26a0' },
  converged: { color: 'text-green-400', icon: '\u2713' },
  completed: { color: 'text-green-400', icon: '\u2713' },
  // Terminal state where the review loop bailed out without converging — warning
  // tone (not a green checkmark) to distinguish it from natural convergence.
  max_rounds_reached: { color: 'text-yellow-400', icon: '\u26a0' },
  failed: { color: 'text-red-400', icon: '\u2715' },
};

const FALLBACK_STYLE = { color: 'text-gray-400', icon: '?' };

function formatRelativeTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

/** Live-updating relative time that ticks every second */
function RelativeTime({ isoDate }: { isoDate: string }) {
  const [elapsed, setElapsed] = useState(() => Date.now() - new Date(isoDate).getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - new Date(isoDate).getTime());
    }, 1000);
    return () => clearInterval(interval);
  }, [isoDate]);

  return <>{formatRelativeTime(elapsed)}</>;
}

export function ReviewList({ reviews, onSelect, onRetry }: ReviewListProps) {
  if (reviews.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No active reviews</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)] border border-gray-800 rounded-lg">
      <table className="w-full text-sm text-left">
        <thead className="sticky top-0 z-10 bg-gray-900 text-xs uppercase text-gray-400 border-b border-gray-700 shadow-[0_1px_0_0_rgb(55,65,81)]">
          <tr>
            <th className="px-4 py-3">PR</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Steps</th>
            <th className="px-4 py-3">Cost</th>
            <th className="px-4 py-3">Time</th>
            <th className="px-4 py-3">Findings</th>
            <th className="px-4 py-3">Last Activity</th>
            <th className="px-4 py-3">Stuck?</th>
            <th className="px-4 py-3">Retry</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review) => {
            const stateStyle = STATE_STYLES[review.state] ?? FALLBACK_STYLE;
            const showSteps = review.stepsCompleted !== undefined && review.stepsTotal !== undefined;
            const showCost = review.totalCostUsd !== undefined && review.totalCostUsd > 0;
            const showDuration = review.totalDurationMs !== undefined && review.totalDurationMs > 0;
            return (
              <tr
                key={review.sessionId}
                onClick={() => onSelect(review.sessionId)}
                className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <a
                    href={review.prUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-400 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    #{review.prNumber}
                  </a>
                  <span className="ml-2 text-gray-400 text-xs truncate max-w-[200px] inline-block align-middle">
                    {review.prTitle}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span data-testid={`state-badge-${review.sessionId}`} className={`inline-flex items-center gap-1 ${stateStyle.color}`}>
                    <span>{stateStyle.icon}</span>
                    <span className="capitalize">{review.state}</span>
                  </span>
                </td>
                <td
                  data-testid={`steps-${review.sessionId}`}
                  className="px-4 py-3 font-mono text-gray-300"
                >
                  {showSteps ? `${review.stepsCompleted}/${review.stepsTotal}` : '—'}
                </td>
                <td
                  data-testid={`cost-${review.sessionId}`}
                  className="px-4 py-3 font-mono text-gray-300"
                >
                  {showCost ? formatCost(review.totalCostUsd!) : '—'}
                </td>
                <td
                  data-testid={`duration-${review.sessionId}`}
                  className="px-4 py-3 font-mono text-gray-400"
                >
                  {showDuration ? formatDuration(review.totalDurationMs!) : '—'}
                </td>
                <td className="px-4 py-3">
                  <FindingSummary
                    open={review.findings.open}
                    fixed={review.findings.fixed}
                    dismissed={review.findings.dismissed}
                    deferred={review.findings.deferred ?? 0}
                  />
                </td>
                <td className="px-4 py-3 text-gray-400">
                  <RelativeTime isoDate={review.lastActivityAt} />
                </td>
                <td className="px-4 py-3">
                  <StuckIndicator lastActivityAt={review.lastActivityAt} />
                </td>
                <td className="px-4 py-3">
                  {review.canRetry && review.failedStep && onRetry ? (
                    <button
                      data-testid={`retry-${review.sessionId}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry(review.sessionId, review.failedStep!);
                      }}
                      className="rounded bg-yellow-700 px-2 py-1 text-xs font-medium text-white hover:bg-yellow-600"
                    >
                      Retry
                    </button>
                  ) : (
                    <span className="text-xs text-gray-600">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
